import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Container } from "./container";
import { cn } from "../../lib/utils";

// Custom button component to replace the deleted Button component
const NavButton = ({ children, variant = "primary", className = "", onClick, ...props }) => {
  const baseStyles =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background";

  const variantStyles = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 h-10 py-2 px-4",
    ghost: "hover:bg-gray-100 text-gray-700 h-10 py-2 px-4",
    outline: "border border-gray-300 bg-transparent hover:bg-gray-50 text-gray-700 h-10 py-2 px-4",
  };

  return (
    <button className={cn(baseStyles, variantStyles[variant], className)} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

export function PublicNavbar({ className, ..._props }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const history = useHistory();

  const isActive = path => location.pathname === path;

  // Auto-hide header on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // At the top of the page - always show header
      if (currentScrollY <= 10) {
        setIsHeaderVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }

      // Scrolling down - hide header
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }
      // Scrolling up - show header
      else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY, isHeaderVisible]);

  // Main navigation items strictly matching the exact screenshot
  const navItems = [
    { label: "Work ▾", href: "/work" },
    { label: "Stay ▾", href: "/stay" },
    { label: "Play ▾", href: "/play" },
    { label: "Move ▾", href: "/move" },
    { label: "Eat ▾", href: "/eat" },
  ];

  const handleNavigation = path => {
    history.push(path);
    setIsOpen(false);
  };

  const handleExternalNavigation = url => {
    window.location.href = url;
    setIsOpen(false);
  };

  // Only the category landing pages open on a full-bleed dark photo; everywhere else the page
  // starts light, so the navbar defaults to dark text (a white logo was invisible on, for
  // example, /enterprise-ready and /host-experience).
  const DARK_HERO_ROUTES = ["/work", "/stay", "/play", "/move", "/eat"];
  const isLightHeroRoute = !DARK_HERO_ROUTES.includes(location.pathname);

  // Panel markup for the mobile menu; portalled into <body> below.
  const menuPanel = (
    <div
      className={cn(
        "fixed inset-0 z-[45] flex flex-col justify-between bg-[#072654]/95 p-8 pt-28 backdrop-blur-2xl transition-all duration-300 ease-in-out md:hidden",
        isOpen ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none -translate-y-4 opacity-0"
      )}
      id="mobile-menu"
    >
      <div className="space-y-6">
        <p className="text-[10px] font-bold tracking-[0.2em] text-blue-300 uppercase border-b border-white/10 pb-3">
          EXPLORE SPACIA
        </p>

        <div className="flex flex-col space-y-4">
          {navItems.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "text-3xl font-extrabold transition-all tracking-tight flex items-center justify-between group",
                isActive(item.href) ? "text-white translate-x-2" : "text-slate-300 hover:text-white"
              )}
              onClick={() => setIsOpen(false)}
            >
              <span>{item.label.replace(" ▾", "")}</span>
              <span className="text-sm font-bold text-blue-400 group-hover:translate-x-1 transition-transform">➔</span>
            </Link>
          ))}

          {/* Pricing and Platform are hidden for now: they are not in the new design.
                Restore by uncommenting (the /pricing and /platform pages still exist).
            <div className="pt-4 flex items-center gap-4">
              <Link
                to="/pricing"
                className="text-lg font-bold text-slate-200 hover:text-white"
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
              <span className="text-white/20">•</span>
              <Link
                to="/platform"
                className="text-lg font-bold text-slate-200 hover:text-white"
                onClick={() => setIsOpen(false)}
              >
                Platform
              </Link>
            </div>
            */}
        </div>
      </div>

      <div className="space-y-4 pt-8 border-t border-white/10">
        <button
          onClick={() => handleNavigation("/package-signup")}
          className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-base py-4 rounded-full shadow-2xl text-center transition-all flex items-center justify-center gap-2"
        >
          Get Started ➔
        </button>

        <button
          onClick={() => handleNavigation("/partner-signup")}
          className="w-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-sm py-3.5 rounded-full text-center transition-all"
        >
          List Your Space
        </button>

        <p className="text-[10px] text-center text-slate-400 font-medium pt-2">
          © 2026 SPACIA Ltd. Developed by theSOFTtribe.
        </p>
      </div>
    </div>
  );

  const mobileMenu = typeof document === "undefined" ? null : createPortal(menuPanel, document.body);

  return (
    <header
      className={cn(
        "fixed top-0 z-50 w-full transition-transform duration-300 ease-in-out px-6 sm:px-12 py-6",
        isHeaderVisible ? "translate-y-0" : "-translate-y-full",
        isLightHeroRoute && !isOpen ? "bg-white/90 backdrop-blur-md" : "bg-transparent",
        className
      )}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center h-12">
        {/* Logo - SPACIA Logo */}
        <div className="flex gap-2 items-center">
          <Link
            to="/home"
            className={cn(
              "text-2xl font-extrabold tracking-[0.25em] transition-colors",
              isLightHeroRoute && !isOpen ? "text-slate-900" : "text-white"
            )}
          >
            SPACIA
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden gap-8 items-center md:flex">
          {navItems.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "text-sm font-medium transition-colors",
                isActive(item.href)
                  ? isLightHeroRoute
                    ? "text-blue-600 font-bold border-b-2 border-blue-600 pb-1"
                    : "text-white font-bold border-b-2 border-white pb-1"
                  : isLightHeroRoute
                    ? "text-slate-700 hover:text-blue-600"
                    : "text-white/80 hover:text-white"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Desktop Action Buttons */}
        <div className="hidden gap-3 items-center md:flex">
          {/* Pricing and Platform are hidden for now: they are not in the new design.
              Restore by uncommenting (the /pricing and /platform pages still exist).
          <Link
            to="/pricing"
            className={cn(
              "text-xs font-semibold px-3 py-1.5 rounded-full transition-all border",
              isLightHeroRoute
                ? "bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200"
                : "bg-white/20 hover:bg-white/30 backdrop-blur-md text-white border-white/20"
            )}
          >
            Pricing
          </Link>
          <Link
            to="/platform"
            className={cn(
              "text-xs font-semibold transition-colors",
              isLightHeroRoute ? "text-slate-700 hover:text-blue-600" : "text-white/90 hover:text-white"
            )}
          >
            Platform
          </Link>
          */}
          <button
            onClick={() => handleNavigation("/partner-signup")}
            className={cn(
              "text-sm font-medium transition-colors",
              isLightHeroRoute ? "text-slate-700 hover:text-blue-600" : "text-white/90 hover:text-white"
            )}
          >
            List Your Space
          </button>
          <button
            onClick={() => handleNavigation("/package-signup")}
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm px-6 py-2.5 rounded-full shadow-lg transition-all"
          >
            Get Started
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={cn(
            "inline-flex justify-center items-center p-2 rounded-full md:hidden backdrop-blur-md border transition-all z-50",
            isLightHeroRoute && !isOpen
              ? "text-slate-900 bg-slate-100/80 border-slate-200 hover:bg-slate-200"
              : "text-white bg-white/10 border-white/20 hover:bg-white/20"
          )}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls="mobile-menu"
          aria-label="Toggle menu"
        >
          <span className="sr-only">Open main menu</span>
          {isOpen ? (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Full-Screen Sleek Mobile Menu.
          Rendered into <body>: the header's backdrop-blur and transform make it the containing
          block for fixed children, which would otherwise clip this panel to the header's height.
          Sits just below the header (z-50) so the close button stays tappable. */}
      {mobileMenu}
    </header>
  );
}
